<template>
  <div class="d-flex align-items-center gap-2">
    <template v-if="reportState === 'success'">
      <div class="btn-group btn-group-sm" :title="generatedAtText">
        <button type="button" class="btn btn-success" @click="downloadReport">
          <FontAwesomeIcon icon="download" class="me-1" />
          {{ $t("production_strategy.download_report") }}
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="regenerate"
        >
          <FontAwesomeIcon icon="sync" class="me-1" />
          {{ $t("production_strategy.generate_new_report") }}
        </button>
      </div>
    </template>

    <template v-else-if="isProcessing">
      <span class="small text-muted text-end">
        {{ $t("production_strategy.report_duration_hint") }}
      </span>
      <button type="button" class="btn btn-sm btn-secondary" disabled>
        <FontAwesomeIcon icon="spinner" class="fa-spin me-1" />
        {{ progressText }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-link text-muted"
        @click="cancelReport"
      >
        {{ $t("common.cancel") }}
      </button>
    </template>

    <button
      v-else-if="reportState === 'failed'"
      type="button"
      class="btn btn-sm btn-danger"
      @click="reset"
    >
      <FontAwesomeIcon icon="exclamation-triangle" class="me-1" />
      {{ $t("production_strategy.report_failed") }}
    </button>

    <button
      v-else
      type="button"
      class="btn btn-sm btn-primary"
      @click="triggerReport"
    >
      <FontAwesomeIcon icon="file-excel" class="me-1" />
      {{ $t("production_strategy.bulk_issuance_report") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { formatDateTime } from "@/composables/formatUtils";
import { useBulkIssuanceReport } from "../composables/useBulkIssuanceReport";
import { computed, toRef } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  accountId: number;
}>();

const { t } = useI18n();

const {
  state: reportState,
  progress,
  completedAt,
  triggerReport,
  downloadReport,
  cancelReport,
  reset,
} = useBulkIssuanceReport(toRef(props, "accountId"));

const isProcessing = computed(
  () => reportState.value === "starting" || reportState.value === "processing"
);

const progressText = computed(() => {
  // The cancel endpoint stores `{cancel_requested: true}` as the task's progress meta, so the
  // counters are only trustworthy once the job itself has reported them.
  if (typeof progress.value?.total !== "number") {
    return t("production_strategy.generating_report");
  }
  return t("production_strategy.generating_report_progress", {
    current: progress.value.current + 1,
    total: progress.value.total,
  });
});

const generatedAtText = computed(() =>
  completedAt.value
    ? t("production_strategy.report_generated_at", {
        time: formatDateTime(completedAt.value),
      })
    : ""
);

const regenerate = async () => {
  reset();
  await triggerReport();
};
</script>
